/**
 * AI Library Mixin
 * Handles prompt library functionality
 */

const ai_library_mixin = {
    data() {
        return {
            // Library state
            library_modal_open: false,
            
            // Library data
            prompt_categories: [
                {
                    id: 1,
                    name: 'Characters',
                    color: '#ec4899', // Pink
                    is_default: true
                }
            ],
            prompt_cards: [],
            
            // UI state
            selected_category: 1,
            library_loading: false,
            
            // New card creation
            creating_new_card: false,
            new_card: {
                name: '',
                category_id: 1,
                prompt_fields: [
                    {
                        id: Date.now(),
                        name: 'Main Prompt',
                        content: ''
                    }
                ],
                generation_settings: {
                    model: 'mikomic_anime_base_v3.safetensors',
                    cfg_scale: 7,
                    steps: 20,
                    use_custom_negative: false,
                    negative_prompt: ''
                },
                thumbnail_image: null
            },
            
            // Category management
            creating_new_category: false,
            new_category_name: '',
            category_colors: [
                '#ec4899', // Pink
                '#8b5cf6', // Purple
                '#06b6d4', // Cyan
                '#10b981', // Emerald
                '#f59e0b', // Amber
                '#ef4444', // Red
                '#3b82f6', // Blue
                '#84cc16'  // Lime
            ]
        };
    },

    methods: {
        /**
         * Show prompt library manager
         */
        show_prompt_library_manager() {
            // Check premium access
            if (!this.check_ai_library_access()) {
                this.show_notification(this.get_t('ai.library_premium_required'), 'warning');
                return;
            }
            
            this.library_modal_open = true;
            this.load_prompt_library();
        },

        /**
         * Close prompt library manager
         */
        close_prompt_library_manager() {
            this.library_modal_open = false;
            this.creating_new_card = false;
            this.creating_new_category = false;
        },

        /**
         * Check if user has access to AI library
         */
        check_ai_library_access() {
            // Free tier has no access to prompt library
            return this.user && this.user.subscription_status !== 'free';
        },

        /**
         * Load prompt library data
         */
        async load_prompt_library() {
            this.library_loading = true;
            
            try {
                //TODO CAMBIAR TODOS LOS MALDITOS FETCH POR FORMDATA COMO EN TODA LA APLICACION
                const response = await fetch('./ai_api/ai_generation.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'load_prompt_library',
                        user_id: this.user.id
                    })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    this.prompt_categories = data.categories || this.prompt_categories;
                    this.prompt_cards = data.cards || [];
                }
                
            } catch (error) {
                console.error('Error loading prompt library:', error);
            } finally {
                this.library_loading = false;
            }
        },

        /**
         * Create new category
         */
        async create_prompt_category() {
            if (!this.new_category_name.trim()) return;
            
            const new_category = {
                id: Date.now(),
                name: this.new_category_name,
                color: this.category_colors[Math.floor(Math.random() * this.category_colors.length)],
                is_default: false
            };
            
            try {
                const response = await fetch('./ai_api/ai_generation.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'create_category',
                        user_id: this.user.id,
                        category: new_category
                    })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    this.prompt_categories.push(new_category);
                    this.new_category_name = '';
                    this.creating_new_category = false;
                    this.show_notification(this.get_t('ai.category_created'), 'success');
                }
                
            } catch (error) {
                console.error('Error creating category:', error);
                this.show_notification(this.get_t('ai.category_creation_error'), 'error');
            }
        },

        /**
         * Delete category
         */
        async delete_category(category_id) {
            if (this.prompt_categories.find(c => c.id === category_id)?.is_default) {
                this.show_notification(this.get_t('ai.cannot_delete_default_category'), 'warning');
                return;
            }
            
            if (!confirm(this.get_t('ai.confirm_delete_category'))) return;
            
            try {
                const response = await fetch('./ai_api/ai_generation.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'delete_category',
                        user_id: this.user.id,
                        category_id: category_id
                    })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    this.prompt_categories = this.prompt_categories.filter(c => c.id !== category_id);
                    this.prompt_cards = this.prompt_cards.filter(c => c.category_id !== category_id);
                    
                    if (this.selected_category === category_id) {
                        this.selected_category = 1; // Default to Characters category
                    }
                    
                    this.show_notification(this.get_t('ai.category_deleted'), 'success');
                }
                
            } catch (error) {
                console.error('Error deleting category:', error);
                this.show_notification(this.get_t('ai.category_deletion_error'), 'error');
            }
        },

        /**
         * Create new prompt card
         */
        start_creating_new_card() {
            this.creating_new_card = true;
            this.new_card = {
                name: '',
                category_id: this.selected_category,
                prompt_fields: [
                    {
                        id: Date.now(),
                        name: 'Main Prompt',
                        content: ''
                    }
                ],
                generation_settings: {
                    model: 'mikomic_anime_base_v3.safetensors',
                    cfg_scale: 7,
                    steps: 20,
                    use_custom_negative: false,
                    negative_prompt: ''
                },
                thumbnail_image: null
            };
        },

        /**
         * Add new prompt field to card
         */
        add_prompt_field() {
            this.new_card.prompt_fields.push({
                id: Date.now(),
                name: `Field ${this.new_card.prompt_fields.length + 1}`,
                content: ''
            });
        },

        /**
         * Remove prompt field from card
         */
        remove_prompt_field(field_id) {
            this.new_card.prompt_fields = this.new_card.prompt_fields.filter(f => f.id !== field_id);
        },

        /**
         * Save new prompt card
         */
        async save_prompt_card() {
            if (!this.new_card.name.trim()) {
                this.show_notification(this.get_t('ai.card_name_required'), 'warning');
                return;
            }
            
            try {
                const response = await fetch('./ai_api/ai_generation.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'create_prompt_card',
                        user_id: this.user.id,
                        card: this.new_card
                    })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    this.prompt_cards.push({
                        ...this.new_card,
                        id: data.card_id
                    });
                    
                    this.creating_new_card = false;
                    this.show_notification(this.get_t('ai.card_created'), 'success');
                }
                
            } catch (error) {
                console.error('Error saving prompt card:', error);
                this.show_notification(this.get_t('ai.card_creation_error'), 'error');
            }
        },

        /**
         * Insert prompt card (full insert)
         */
        insert_full_prompt_card(card) {
            // Insert all prompt fields
            const combined_prompt = card.prompt_fields.map(f => f.content).join(', ');
            this.ai_prompts.positive = combined_prompt;
            
            // Apply generation settings
            Object.assign(this.ai_settings, card.generation_settings);
            
            this.close_prompt_library_manager();
            this.show_notification(this.get_t('ai.prompt_inserted'), 'success');
        },

        /**
         * Insert specific prompt field
         */
        insert_prompt_field(field) {
            const current_prompt = this.ai_prompts.positive;
            const separator = current_prompt && !current_prompt.endsWith(', ') ? ', ' : '';
            this.ai_prompts.positive = current_prompt + separator + field.content;
            
            this.close_prompt_library_manager();
            this.show_notification(this.get_t('ai.prompt_field_inserted'), 'success');
        },

        /**
         * Get filtered cards for current category
         */
        get_filtered_cards() {
            return this.prompt_cards.filter(card => card.category_id === this.selected_category);
        },

        /**
         * Cancel card creation
         */
        cancel_card_creation() {
            this.creating_new_card = false;
        },

        /**
         * Cancel category creation
         */
        cancel_category_creation() {
            this.creating_new_category = false;
            this.new_category_name = '';
        }
    }
};

export default ai_library_mixin;